import {
  Dialog, DialogContent, DialogActions,
} from '@mui/material';
import { NETWORKS, NETWORK_IDS } from 'lib/constants';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { isGondolaApp, isGondolaAppVersionAtLeast, sendAuthRequestToApp } from 'lib/utils/mobileAppUtils';
import { useCallback } from 'react';

interface Props {
  action: string;
  network: number;
  onClose: () => void;
  /** Callback when the user attempts to connect to the network */
  onConnect: () => void;
}

const DEFAULT_POST_TYPE = 'posts, photos, and videos';

const PostTypes: Record<number, string> = {
  [NETWORK_IDS.tiktok]: 'videos',
  [NETWORK_IDS.twitter]: 'tweets',
  [NETWORK_IDS.twitch]: 'videos',
  [NETWORK_IDS.youtube]: 'videos',
};

// Move outside component to prevent recreation
const getBaseUrl = (network: number) => {
  if (network === NETWORK_IDS.twitter) return '/api/auth/twitter/v2';
  if (network === NETWORK_IDS.tiktok) return '/api/auth/tiktok';
  if (network === NETWORK_IDS.twitch) return '/api/auth/twitch';
  if (network === NETWORK_IDS.youtube) return '/api/auth/youtube';
  if (network === NETWORK_IDS.linkedin) return '/api/auth/linkedin';
  if (network === NETWORK_IDS.threads) return '/api/auth/threads';
  if (network === NETWORK_IDS.instagram) return '/api/auth/facebook';
  return '/api/auth/facebook';
};

export const SimpleConnectAccountModal = ({
  action, network, onClose, onConnect,
}: Props) => {
  const currentUser = useCurrentUser();
  const networkTitle = NETWORKS[network].title;

  const url = useCallback((next?: string) => {
    const params = [];
    if (currentUser?.id) {
      params.push(['userId', currentUser.id.toString()]);
    }
    if (next) {
      params.push(['next', next]);
    }

    const searchParams = new URLSearchParams(params);
    return `${getBaseUrl(network)}?${searchParams.toString()}`;
  }, [network, currentUser?.id]);

  const handleThreadsConnect = useCallback(() => {
    onConnect?.();
    sendAuthRequestToApp('threads', url());
  }, [onConnect, url]);

  const renderButton = () => {
    if (isGondolaApp && isGondolaAppVersionAtLeast('1.0.14') && network === NETWORK_IDS.threads) {
      // if we're in the Gondola app, we need to prevent opnening a deep link to the Threads app
      return (
        <button
          className="btn-primary mr-2"
          type="button"
          onClick={handleThreadsConnect}
        >
          {action}
        </button>
      );
    }
    return (
      <a
        className="btn-primary mr-2"
        href={url(window.location.pathname)}
      >
        Connect
      </a>
    );
  };
  return (
    <Dialog open maxWidth="md">
      <div className="p-6">
        <div className="relative">
          <h1 className="text-lg font-bold">
            Connect
            {' '}
            {networkTitle}
          </h1>
        </div>
        <DialogContent>
          <p className="mb-2">
            By connecting the
            {' '}
            {networkTitle}
            {' '}
            accounts you manage to Gondola,
            we can automatically import
            {' '}
            {PostTypes[network] || DEFAULT_POST_TYPE}
            {' '}
            so you can get credit.
          </p>
          <p className="mb-2">
            We only request permission to read
            {' '}
            {PostTypes[network] || DEFAULT_POST_TYPE}
            {' '}
            -- we never create or update them.
            You can choose the
            {' '}
            {network === NETWORK_IDS.facebook && 'pages and'}
            {' '}
            accounts Gondola may access when you connect.
            (And you may change your mind and revoke permission at any time.)
          </p>
        </DialogContent>
        <DialogActions>
          <button
            onClick={onClose}
            color="inherit"
            type="button"
            className="btn-primary-outlined mr-2"
          >
            Close
          </button>
          {renderButton()}
        </DialogActions>
      </div>
    </Dialog>
  );
};
